import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  LogOut, Save, RotateCcw, Inbox, FileJson, Trash2, Mail, Phone, Globe,
  Loader2, Send, FolderOpen, Users, Bell, BellOff, Plus,
  CalendarClock, CalendarCog, Video, Copy, X,
  KeyRound, UserCircle, Briefcase, MessageCircle, Newspaper, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";
import { useInquiryAlerts } from "@/hooks/useInquiryAlerts";
import { useAppointmentAlerts } from "@/hooks/useAppointmentAlerts";
import { useChatAlerts } from "@/hooks/useChatAlerts";
import { useDocuments } from "@/hooks/useDocuments";
import { useAdLeadAlerts } from "@/hooks/useAdLeadAlerts";
import { useStaffHeartbeat } from "@/hooks/useStaffHeartbeat";
import { useBulkSelect } from "@/hooks/useBulkSelect";
import ChangePasswordForm from "@/components/ChangePasswordForm";
import CasesPanel from "@/components/admin/CasesPanel";
import DocumentRequirementsPanel from "@/components/admin/DocumentRequirementsPanel";
import ChatPanel from "@/components/admin/ChatPanel";
import DocumentsPanel from "@/components/admin/DocumentsPanel";
import AdLeadsPanel from "@/components/admin/AdLeadsPanel";
import BlogPanel from "@/components/admin/BlogPanel";

const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

export default function AdminDashboardPage() {
  const { user, checking, logout, updateUser } = useAuth();
  const { refresh } = useContent();
  const { t } = useLang();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const highlightSubmissionId = params.get("submission");

  const [tab, setTab] = useState(() => params.get("tab") || "content");
  const [contentJson, setContentJson] = useState("");
  const [loadingContent, setLoadingContent] = useState(true);
  const [saving, setSaving] = useState(false);

  const { inquiries, setInquiries, loadingInq, soundArmed, armSound } = useInquiryAlerts(user);
  // Sound alerts share one global AudioContext (see lib/beep.js), so arming it once
  // via the header button covers inquiry, appointment, chat, and ad-lead alerts.
  const { appointments, setAppointments, loadingAppts } = useAppointmentAlerts(user);
  const { conversations, loadingChats } = useChatAlerts(user);
  const { documents, setDocuments, loadingDocs } = useDocuments(user);
  const { leads, setLeads, loadingLeads } = useAdLeadAlerts(user);
  useStaffHeartbeat(user);

  const inquirySelect = useBulkSelect();
  const appointmentSelect = useBulkSelect();

  const pendingSubmissionCount = documents.filter((d) => d.files.some((f) => f.status === "pending")).length;

  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: "", password: "", role: "sub_admin" });

  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [newBlackoutDate, setNewBlackoutDate] = useState("");
  const [sendingReminders, setSendingReminders] = useState(false);

  const [profileName, setProfileName] = useState(user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const loadContent = useCallback(async () => {
    setLoadingContent(true);
    try {
      const { data } = await api.get("/admin/content");
      setContentJson(JSON.stringify(data.data, null, 2));
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoadingContent(false);
    }
  }, []);

  const loadStaff = useCallback(async () => {
    setLoadingStaff(true);
    try {
      const { data } = await api.get("/admin/users");
      setStaff(data.items || []);
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoadingStaff(false);
    }
  }, []);

  const loadAvailability = useCallback(async () => {
    setLoadingAvailability(true);
    try {
      const { data } = await api.get("/admin/availability");
      setAvailability(data);
    } catch {
      toast.error("Failed to load availability");
    } finally {
      setLoadingAvailability(false);
    }
  }, []);

  useEffect(() => {
    if (!checking && user && isAdmin) {
      loadContent();
      loadStaff();
      loadAvailability();
    }
  }, [checking, user, isAdmin, loadContent, loadStaff, loadAvailability]);

  useEffect(() => {
    if (user && !isAdmin && (tab === "content" || tab === "staff" || tab === "availability" || tab === "requirements")) {
      setTab("inquiries");
    }
  }, [user, isAdmin, tab]);

  useEffect(() => {
    setProfileName(user?.name || "");
  }, [user?.name]);

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--bg)]">
        <Loader2 size={32} className="text-[var(--gold)] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  if (user.must_change_password) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--bg)] px-4">
        <div className="card-dark p-8 max-w-md w-full text-center" data-testid="forced-password-change">
          <div className="w-14 h-14 mx-auto mb-4 grid place-items-center rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/30">
            <KeyRound size={22} className="text-[var(--gold)]" />
          </div>
          <h1 className="font-display text-xl font-bold text-white mb-2">Set a new password</h1>
          <p className="text-white/60 text-sm mb-6">
            You're signed in with a temporary password. Choose a new one to continue to your dashboard.
          </p>
          <ChangePasswordForm
            submitLabel="Set Password & Continue"
            onSuccess={() => updateUser({ must_change_password: false })}
          />
        </div>
      </div>
    );
  }

  async function saveContent() {
    let parsed;
    try {
      parsed = JSON.parse(contentJson);
    } catch {
      toast.error(t("admin.invalid"));
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/content", { content: parsed });
      toast.success(t("admin.saved"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function resetContent() {
    if (!window.confirm("Reset content to defaults? This will overwrite all changes.")) return;
    try {
      await api.post("/admin/content/reset");
      toast.success("Content reset to default");
      await loadContent();
      await refresh();
    } catch {
      toast.error("Reset failed");
    }
  }

  async function deleteInquiry(id) {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  async function bulkDeleteInquiries() {
    const ids = [...inquirySelect.selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected inquir${ids.length === 1 ? "y" : "ies"}? This cannot be undone.`)) return;
    try {
      await api.post("/admin/inquiries/bulk-delete", { ids });
      setInquiries((s) => s.filter((x) => !ids.includes(x.id)));
      inquirySelect.clear();
      toast.success(`Deleted ${ids.length} inquir${ids.length === 1 ? "y" : "ies"}`);
    } catch {
      toast.error("Bulk delete failed");
    }
  }

  async function sendTestEmail() {
    try {
      const { data } = await api.post("/admin/test-email");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test email failed");
    }
  }

  async function createStaff(e) {
    e.preventDefault();
    setCreatingStaff(true);
    try {
      await api.post("/admin/users", staffForm);
      toast.success("Staff account created");
      setStaffForm({ email: "", password: "", role: "sub_admin" });
      await loadStaff();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create account");
    } finally {
      setCreatingStaff(false);
    }
  }

  async function deleteStaff(email) {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      setStaff((s) => s.filter((x) => x.email !== email));
      toast.success("Removed");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not remove account");
    }
  }

  async function cancelAppointment(id) {
    if (!window.confirm("Cancel this appointment? The client will be notified by email.")) return;
    try {
      await api.post(`/admin/appointments/${id}/cancel`);
      setAppointments((s) => s.map((a) => (a.id === id ? { ...a, status: "cancelled" } : a)));
      toast.success("Appointment cancelled");
    } catch {
      toast.error("Could not cancel appointment");
    }
  }

  async function deleteAppointment(id) {
    if (!window.confirm("Permanently delete this appointment? The client will NOT be notified. This cannot be undone.")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      setAppointments((s) => s.filter((a) => a.id !== id));
      toast.success("Appointment deleted");
    } catch {
      toast.error("Could not delete appointment");
    }
  }

  async function bulkDeleteAppointments() {
    const ids = [...appointmentSelect.selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected appointment(s)? Clients will NOT be notified. This cannot be undone.`)) return;
    try {
      await api.post("/admin/appointments/bulk-delete", { ids });
      setAppointments((s) => s.filter((a) => !ids.includes(a.id)));
      appointmentSelect.clear();
      toast.success(`Deleted ${ids.length} appointment(s)`);
    } catch {
      toast.error("Bulk delete failed");
    }
  }

  function updateWeeklyRange(day, field, value) {
    setAvailability((a) => {
      const ranges = a.weekly_hours[day]?.length ? [...a.weekly_hours[day]] : [{ start: "09:00", end: "18:00" }];
      ranges[0] = { ...ranges[0], [field]: value };
      return { ...a, weekly_hours: { ...a.weekly_hours, [day]: ranges } };
    });
  }

  function toggleDayEnabled(day, enabled) {
    setAvailability((a) => ({
      ...a,
      weekly_hours: { ...a.weekly_hours, [day]: enabled ? [{ start: "09:00", end: "18:00" }] : [] },
    }));
  }

  function addBlackoutDate() {
    if (!newBlackoutDate) return;
    setAvailability((a) => {
      if (a.blackout_dates.includes(newBlackoutDate)) return a;
      return { ...a, blackout_dates: [...a.blackout_dates, newBlackoutDate].sort() };
    });
    setNewBlackoutDate("");
  }

  function removeBlackoutDate(date) {
    setAvailability((a) => ({ ...a, blackout_dates: a.blackout_dates.filter((d) => d !== date) }));
  }

  async function saveAvailability() {
    setSavingAvailability(true);
    try {
      const { data } = await api.put("/admin/availability", availability);
      setAvailability(data);
      toast.success("Availability saved");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSavingAvailability(false);
    }
  }

  function copyFeedUrl() {
    const url = `${API}/admin/appointments/feed.ics?token=${user.ics_token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Calendar feed URL copied"),
      () => toast.error("Couldn't copy — copy it manually")
    );
  }

  async function sendReminders() {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/admin/appointments/send-reminders");
      toast.success(`Checked ${data.checked}, sent ${data.sent} reminder${data.sent === 1 ? "" : "s"}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send reminders");
    } finally {
      setSendingReminders(false);
    }
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { name: profileName });
      updateUser({ name: profileName });
      toast.success("Profile updated");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="sticky top-0 z-30 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img
              src="/logo.png"
              alt="Eazy-Travels"
              className="h-9 w-auto object-contain"
            />
            <div className="hidden sm:block border-l border-white/10 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--gold)]">{t("admin.dashboard")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={armSound}
              className={`text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5 transition ${
                soundArmed
                  ? "border-[var(--gold)]/40 text-[var(--gold)]"
                  : "border-white/15 text-white/50 hover:text-white"
              }`}
              title={soundArmed ? "Sound alerts enabled" : "Click to enable a sound alert for new inquiries"}
              data-testid="admin-arm-sound"
            >
              {soundArmed ? <Bell size={13} /> : <BellOff size={13} />}
              {soundArmed ? "Alerts on" : "Enable sound alerts"}
            </button>
            <span className="hidden sm:inline text-xs text-white/50">{user.email}</span>
            <button
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="btn-outline-gold text-sm py-2 px-4"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-6 border-b border-white/5 flex-wrap">
          {isAdmin && (
            <button
              onClick={() => setTab("content")}
              className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "content" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid="admin-tab-content"
            >
              <FileJson size={15} className="inline mr-1.5" /> {t("admin.content")}
            </button>
          )}
          <button
            onClick={() => setTab("inquiries")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "inquiries" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-inquiries"
          >
            <Inbox size={15} className="inline mr-1.5" /> {t("admin.inquiries")} ({inquiries.length})
          </button>
          <button
            onClick={() => setTab("ad-leads")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "ad-leads" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-ad-leads"
          >
            <Megaphone size={15} className="inline mr-1.5" /> Ad Leads ({leads.length})
          </button>
          <button
            onClick={() => setTab("documents")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "documents" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-documents"
          >
            <FolderOpen size={15} className="inline mr-1.5" /> Documents ({documents.length})
            {pendingSubmissionCount > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-[var(--gold)] text-[#0a0a0a] text-[11px] font-bold align-middle"
                data-testid="admin-documents-pending-badge"
              >
                {pendingSubmissionCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("appointments")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "appointments" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-appointments"
          >
            <CalendarClock size={15} className="inline mr-1.5" /> Appointments ({appointments.filter((a) => a.status !== "cancelled").length})
          </button>
          <button
            onClick={() => setTab("cases")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "cases" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-cases"
          >
            <Briefcase size={15} className="inline mr-1.5" /> Cases
          </button>
          <button
            onClick={() => setTab("chat")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "chat" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-chat"
          >
            <MessageCircle size={15} className="inline mr-1.5" /> Chat
            {conversations.filter((c) => c.unread_by_staff).length > 0 && (
              <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-[var(--gold)]" />
            )}
          </button>
          <button
            onClick={() => setTab("blog")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "blog" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-blog"
          >
            <Newspaper size={15} className="inline mr-1.5" /> Blog
          </button>
          {isAdmin && (
            <button
              onClick={() => setTab("availability")}
              className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "availability" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid="admin-tab-availability"
            >
              <CalendarCog size={15} className="inline mr-1.5" /> Availability
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setTab("requirements")}
              className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "requirements" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid="admin-tab-requirements"
            >
              <FolderOpen size={15} className="inline mr-1.5" /> Requirements
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setTab("staff")}
              className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
                tab === "staff" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
              }`}
              data-testid="admin-tab-staff"
            >
              <Users size={15} className="inline mr-1.5" /> Staff ({staff.length})
            </button>
          )}
          <button
            onClick={() => setTab("profile")}
            className={`px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              tab === "profile" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid="admin-tab-profile"
          >
            <UserCircle size={15} className="inline mr-1.5" /> Profile
          </button>
          {isAdmin && (
            <div className="ml-auto flex items-center">
              <button
                onClick={sendTestEmail}
                className="text-xs text-white/55 hover:text-[var(--gold)] px-3 py-2 inline-flex items-center gap-1.5 transition"
                data-testid="admin-test-email"
                title="Send a test email using the configured SMTP settings"
              >
                <Send size={12} /> Test SMTP
              </button>
            </div>
          )}
        </div>

        {tab === "content" && isAdmin && (
          <div className="card-dark p-6" data-testid="admin-content-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-white">{t("admin.contentEditor")}</h2>
                <p className="text-sm text-white/55 mt-1">
                  Edit services, countries, testimonials, hero slides, stats and company info — all in JSON.
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={resetContent} className="btn-outline-gold text-sm py-2 px-4" data-testid="admin-reset">
                  <RotateCcw size={14} /> {t("admin.reset")}
                </button>
                <button onClick={saveContent} disabled={saving} className="btn-gold text-sm py-2 px-5" data-testid="admin-save">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {t("admin.save")}
                </button>
              </div>
            </div>
            {loadingContent ? (
              <div className="h-96 grid place-items-center"><Loader2 size={28} className="text-[var(--gold)] animate-spin" /></div>
            ) : (
              <textarea
                value={contentJson}
                onChange={(e) => setContentJson(e.target.value)}
                className="input-dark font-mono text-xs leading-relaxed min-h-[600px]"
                spellCheck={false}
                data-testid="admin-content-textarea"
              />
            )}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-3" data-testid="admin-inquiries-panel">
            {loadingInq && <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
            {!loadingInq && inquiries.length === 0 && (
              <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-inquiries">
                No inquiries yet. They'll appear here as users submit the contact form.
              </div>
            )}
            {!loadingInq && inquiries.length > 0 && (
              <div className="flex items-center gap-3 mb-1" data-testid="admin-inquiries-bulk-bar">
                <label className="flex items-center gap-2 text-xs text-white/55 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
                    checked={inquirySelect.isAllSelected(inquiries.map((i) => i.id))}
                    onChange={() => inquirySelect.toggleAll(inquiries.map((i) => i.id))}
                    data-testid="admin-inquiries-select-all"
                  />
                  Select all
                </label>
                {inquirySelect.selected.size > 0 && (
                  <button
                    onClick={bulkDeleteInquiries}
                    className="text-xs px-3 py-1.5 rounded-full border border-red-400/30 text-red-400 hover:bg-red-500/10 transition inline-flex items-center gap-1.5"
                    data-testid="admin-inquiries-bulk-delete"
                  >
                    <Trash2 size={12} /> Delete Selected ({inquirySelect.selected.size})
                  </button>
                )}
              </div>
            )}
            {inquiries.map((inq) => (
              <div key={inq.id} className="card-dark p-5 flex gap-3" data-testid={`inquiry-row-${inq.id}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-1 accent-[var(--gold)] cursor-pointer shrink-0"
                  checked={inquirySelect.selected.has(inq.id)}
                  onChange={() => inquirySelect.toggle(inq.id)}
                  data-testid={`inquiry-select-${inq.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-semibold text-white">{inq.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-white/55 mt-1">
                        <a href={`mailto:${inq.email}`} className="flex items-center gap-1 hover:text-[var(--gold)]">
                          <Mail size={12} /> {inq.email}
                        </a>
                        {inq.phone && <span className="flex items-center gap-1"><Phone size={12} /> {inq.phone}</span>}
                        {inq.destination_country && (
                          <span className="flex items-center gap-1"><Globe size={12} /> {inq.destination_country.toUpperCase()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {inq.service_type && (
                        <span className="text-xs px-2.5 py-1 rounded-full border border-[var(--gold)]/25 text-[var(--gold)]">
                          {inq.service_type}
                        </span>
                      )}
                      <span className="text-xs text-white/45">{new Date(inq.created_at).toLocaleString()}</span>
                      <button
                        onClick={() => deleteInquiry(inq.id)}
                        className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete"
                        data-testid={`inquiry-delete-${inq.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{inq.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "ad-leads" && (
          <AdLeadsPanel isAdmin={isAdmin} leads={leads} setLeads={setLeads} loadingLeads={loadingLeads} />
        )}

        {tab === "documents" && (
          <DocumentsPanel
            isAdmin={isAdmin}
            highlightSubmissionId={highlightSubmissionId}
            documents={documents}
            setDocuments={setDocuments}
            loadingDocs={loadingDocs}
          />
        )}

        {tab === "appointments" && (
          <div className="space-y-3" data-testid="admin-appointments-panel">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-1">
              <div className="flex items-center gap-3">
                {!loadingAppts && appointments.length > 0 && (
                  <>
                    <label className="flex items-center gap-2 text-xs text-white/55 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
                        checked={appointmentSelect.isAllSelected(appointments.map((a) => a.id))}
                        onChange={() => appointmentSelect.toggleAll(appointments.map((a) => a.id))}
                        data-testid="admin-appointments-select-all"
                      />
                      Select all
                    </label>
                    {appointmentSelect.selected.size > 0 && (
                      <button
                        onClick={bulkDeleteAppointments}
                        className="text-xs px-3 py-1.5 rounded-full border border-red-400/30 text-red-400 hover:bg-red-500/10 transition inline-flex items-center gap-1.5"
                        data-testid="admin-appointments-bulk-delete"
                      >
                        <Trash2 size={12} /> Delete Selected ({appointmentSelect.selected.size})
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={sendReminders}
                  disabled={sendingReminders}
                  className="text-xs text-white/55 hover:text-[var(--gold)] px-3 py-2 inline-flex items-center gap-1.5 transition disabled:opacity-40"
                  title="Manually run the 24h-reminder job now (normally runs automatically via cron — see README)"
                  data-testid="admin-send-reminders"
                >
                  {sendingReminders ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send reminders now
                </button>
                <button
                  onClick={copyFeedUrl}
                  className="text-xs text-white/55 hover:text-[var(--gold)] px-3 py-2 inline-flex items-center gap-1.5 transition"
                  title="Copy a URL you can subscribe to in Google Calendar / Outlook"
                  data-testid="admin-copy-feed-url"
                >
                  <Copy size={12} /> Subscribe to your calendar
                </button>
              </div>
            </div>
            {loadingAppts && <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
            {!loadingAppts && appointments.length === 0 && (
              <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-appointments">
                No appointments yet. They'll appear here as clients book a consultation.
              </div>
            )}
            {appointments.map((appt) => (
              <div key={appt.id} className={`card-dark p-5 flex gap-3 ${appt.status === "cancelled" ? "opacity-50" : ""}`} data-testid={`appointment-row-${appt.id}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-1 accent-[var(--gold)] cursor-pointer shrink-0"
                  checked={appointmentSelect.selected.has(appt.id)}
                  onChange={() => appointmentSelect.toggle(appt.id)}
                  data-testid={`appointment-select-${appt.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-semibold text-white">{appt.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-white/55 mt-1">
                        <a href={`mailto:${appt.email}`} className="flex items-center gap-1 hover:text-[var(--gold)]">
                          <Mail size={12} /> {appt.email}
                        </a>
                        {appt.phone && <span className="flex items-center gap-1"><Phone size={12} /> {appt.phone}</span>}
                        <span className="flex items-center gap-1">
                          {appt.meeting_preference === "video" ? <Video size={12} /> : <Phone size={12} />}
                          {appt.meeting_preference === "video" ? "Video call" : "Phone call"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${appt.status === "cancelled" ? "border-red-400/30 text-red-400" : "border-[var(--gold)]/25 text-[var(--gold)]"}`}>
                        {appt.status === "cancelled" ? "Cancelled" : new Date(appt.start_time).toLocaleString()}
                      </span>
                      {appt.status !== "cancelled" && (
                        <button
                          onClick={() => cancelAppointment(appt.id)}
                          className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-amber-400 hover:bg-amber-500/10 transition"
                          title="Cancel — notifies the client"
                          data-testid={`appointment-cancel-${appt.id}`}
                        >
                          <X size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteAppointment(appt.id)}
                        className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete permanently — no notification"
                        data-testid={`appointment-delete-${appt.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {appt.message && <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{appt.message}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "cases" && <CasesPanel isAdmin={isAdmin} />}

        {tab === "chat" && <ChatPanel conversations={conversations} loadingChats={loadingChats} />}

        {tab === "blog" && <BlogPanel isAdmin={isAdmin} />}

        {tab === "availability" && isAdmin && (
          <div className="space-y-6" data-testid="admin-availability-panel">
            {loadingAvailability || !availability ? (
              <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>
            ) : (
              <>
                <div className="card-dark p-6">
                  <h2 className="font-display text-xl font-bold text-white mb-4">Booking rules</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <label className="flex items-center gap-2 text-sm text-white/70 lg:col-span-4">
                      <input
                        type="checkbox"
                        checked={availability.enabled}
                        onChange={(e) => setAvailability((a) => ({ ...a, enabled: e.target.checked }))}
                      />
                      Booking enabled
                    </label>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Timezone</label>
                      <input
                        value={availability.timezone}
                        onChange={(e) => setAvailability((a) => ({ ...a, timezone: e.target.value }))}
                        className="input-dark"
                        placeholder="Europe/Bucharest"
                        data-testid="availability-timezone"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Slot length (min)</label>
                      <input
                        type="number" min={5} max={240}
                        value={availability.slot_minutes}
                        onChange={(e) => setAvailability((a) => ({ ...a, slot_minutes: Number(e.target.value) }))}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Min notice (hours)</label>
                      <input
                        type="number" min={0} max={720}
                        value={availability.min_notice_hours}
                        onChange={(e) => setAvailability((a) => ({ ...a, min_notice_hours: Number(e.target.value) }))}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Max advance (days)</label>
                      <input
                        type="number" min={1} max={180}
                        value={availability.max_advance_days}
                        onChange={(e) => setAvailability((a) => ({ ...a, max_advance_days: Number(e.target.value) }))}
                        className="input-dark"
                      />
                    </div>
                  </div>
                </div>

                <div className="card-dark p-6">
                  <h2 className="font-display text-xl font-bold text-white mb-4">Weekly hours</h2>
                  <div className="space-y-2">
                    {WEEKDAYS.map(({ key, label }) => {
                      const enabled = (availability.weekly_hours[key] || []).length > 0;
                      const range = availability.weekly_hours[key]?.[0] || { start: "09:00", end: "18:00" };
                      return (
                        <div key={key} className="flex flex-wrap items-center gap-3">
                          <label className="flex items-center gap-2 text-sm text-white/70 w-32">
                            <input type="checkbox" checked={enabled} onChange={(e) => toggleDayEnabled(key, e.target.checked)} />
                            {label}
                          </label>
                          {enabled && (
                            <>
                              <input
                                type="time"
                                value={range.start}
                                onChange={(e) => updateWeeklyRange(key, "start", e.target.value)}
                                className="input-dark w-32"
                              />
                              <span className="text-white/40 text-sm">to</span>
                              <input
                                type="time"
                                value={range.end}
                                onChange={(e) => updateWeeklyRange(key, "end", e.target.value)}
                                className="input-dark w-32"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="card-dark p-6">
                  <h2 className="font-display text-xl font-bold text-white mb-4">Blackout dates</h2>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {availability.blackout_dates.map((d) => (
                      <span key={d} className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/70 inline-flex items-center gap-1.5">
                        {d}
                        <button onClick={() => removeBlackoutDate(d)} className="hover:text-red-400">
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    {availability.blackout_dates.length === 0 && <span className="text-white/40 text-sm">None set.</span>}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={newBlackoutDate}
                      onChange={(e) => setNewBlackoutDate(e.target.value)}
                      className="input-dark w-auto"
                    />
                    <button onClick={addBlackoutDate} className="btn-outline-gold text-sm py-2 px-4">
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>

                <button onClick={saveAvailability} disabled={savingAvailability} className="btn-gold text-sm py-2 px-5" data-testid="availability-save">
                  {savingAvailability ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save Availability
                </button>
              </>
            )}
          </div>
        )}

        {tab === "requirements" && isAdmin && <DocumentRequirementsPanel />}

        {tab === "staff" && isAdmin && (
          <div className="space-y-6" data-testid="admin-staff-panel">
            <div className="card-dark p-6">
              <h2 className="font-display text-xl font-bold text-white mb-4">Add staff account</h2>
              <form onSubmit={createStaff} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Email</label>
                  <input
                    required
                    type="email"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm((f) => ({ ...f, email: e.target.value }))}
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Password</label>
                  <input
                    required
                    minLength={6}
                    type="password"
                    value={staffForm.password}
                    onChange={(e) => setStaffForm((f) => ({ ...f, password: e.target.value }))}
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Role</label>
                  <select
                    value={staffForm.role}
                    onChange={(e) => setStaffForm((f) => ({ ...f, role: e.target.value }))}
                    className="input-dark"
                  >
                    <option value="sub_admin">Sub-admin</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <button type="submit" disabled={creatingStaff} className="btn-gold text-sm py-2 px-5">
                    {creatingStaff ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Create account
                  </button>
                </div>
              </form>
            </div>

            <div className="space-y-3">
              {loadingStaff && <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
              {!loadingStaff && staff.map((s) => (
                <div key={s.email} className="card-dark p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{s.email}</p>
                    <p className="text-xs text-white/50">{s.role} · joined {new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  {s.email !== user.email && (
                    <button
                      onClick={() => deleteStaff(s.email)}
                      className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "profile" && (
          <div className="space-y-6 max-w-xl" data-testid="admin-profile-panel">
            <div className="card-dark p-6">
              <h2 className="font-display text-xl font-bold text-white mb-4">Your details</h2>
              <form onSubmit={saveProfile} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Email</label>
                  <input value={user.email} disabled className="input-dark opacity-60 cursor-not-allowed" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Display name</label>
                  <input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    className="input-dark"
                    placeholder="Your name"
                    data-testid="profile-name-input"
                  />
                </div>
                <button type="submit" disabled={savingProfile} className="btn-gold text-sm py-2 px-5" data-testid="profile-save">
                  {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save
                </button>
              </form>
            </div>

            <div className="card-dark p-6">
              <h2 className="font-display text-xl font-bold text-white mb-4">Change password</h2>
              <ChangePasswordForm />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
